// © 2026 Holmes Computer Consultants. All rights reserved. -- www.holmesconsultants.com
//
// The light/dark control (M3-37, 08 §3).
//
// **Three positions, not two.** A two-position switch cannot express "follow my phone", and the
// first tap on a two-position switch silently opts the player out of that for good — their
// machine stops going dark at sunset and nothing on screen ever says why. So the ring is
// system → light → dark → system, and the control says which of the three it is on.
//
// The glyph follows the *appearance*, and the label follows the *choice*. That is deliberate: a
// player on `system` at night wants to see a moon (because it is dark) and to read "Following
// your system" (because that is what will happen tomorrow morning).

import { useSyncExternalStore } from 'react'
import type { ThemeController } from '../theme'
import { useLocalisation } from '../i18n/useLanguage'

export function ThemeToggle({ theme }: { theme: ThemeController }): React.JSX.Element {
  const { t } = useLocalisation()
  // `useSyncExternalStore` rather than an effect and a `useState`: the controller is written to
  // by the operating system as well as by this button, and a state that is only updated in the
  // click handler is a moon that stays a sun after sunset.
  const appearance = useSyncExternalStore(
    (onChange) => theme.subscribe(onChange),
    () => theme.appearance,
    () => 'dark' as const,
  )
  // The controller hands back a key and its values; the sentence is assembled here, where there is
  // a translator. `appearance` and `choice` are themselves keys — "currently dark" has to say
  // *sombre* on a French page, not `dark`.
  const said = theme.description
  const description =
    said.key === 'theme.system'
      ? t('theme.system', {
          appearance: t(said.appearance === 'dark' ? 'theme.appearance.dark' : 'theme.appearance.light'),
        })
      : t('theme.always', {
          choice: t(said.choice === 'dark' ? 'theme.appearance.dark' : 'theme.appearance.light'),
        })

  return (
    <button
      type="button"
      className="button button--quiet"
      title={t('theme.toggle-title', { description })}
      aria-live="polite"
      onClick={() => theme.cycle()}
    >
      {appearance === 'dark' ? <MoonGlyph /> : <SunGlyph />}
      <span className="sr-only">{description}</span>
    </button>
  )
}

function SunGlyph(): React.JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="4.2" />
      <path d="M12 2.5v2.6M12 18.9v2.6M2.5 12h2.6M18.9 12h2.6M5.2 5.2l1.9 1.9M16.9 16.9l1.9 1.9M18.8 5.2l-1.9 1.9M7.1 16.9l-1.9 1.9" />
    </svg>
  )
}

function MoonGlyph(): React.JSX.Element {
  return (
    <svg
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      focusable="false"
    >
      <path d="M20 14.6A8.6 8.6 0 0 1 9.4 4a8.6 8.6 0 1 0 10.6 10.6Z" />
    </svg>
  )
}
